/**
 * Text optimization.
 *
 * Hard rule: this module NEVER changes the user's words. It only decides where
 * the line breaks go and which existing words get emphasized. `balanceLines`
 * asserts that its output re-joins to the exact input token sequence, so a bug
 * here surfaces as a thrown error rather than as silently edited copy.
 */

/** Breaking after these reads badly — a line should not end on a function word. */
const WEAK_ENDINGS = new Set([
  'a', 'an', 'the', 'of', 'to', 'in', 'on', 'at', 'by', 'for', 'with',
  'and', 'or', 'but', 'from', 'as', 'is', 'are', 'was', 'were', 'be',
  'through', 'into', 'over', 'under', 'your', 'our', 'my', 'their',
]);

/** Never emphasized — emphasis should land on meaning, not grammar. */
const STOPWORDS = new Set([
  ...WEAK_ENDINGS,
  'that', 'this', 'these', 'those', 'it', 'its', 'we', 'you', 'they',
  'more', 'most', 'very', 'can', 'will', 'has', 'have', 'had', 'not',
]);

export function tokenize(text: string): string[] {
  return text.trim().split(/\s+/).filter(Boolean);
}

/** Approximate advance width in em units. Good enough to rank break options. */
const NARROW = new Set(['i', 'l', 'j', 't', 'f', 'r', 'I', '1', '.', ',', "'", '!', ':', ';', '|']);
const WIDE = new Set(['m', 'w', 'M', 'W', '@', '%', '—', '#']);

export function estimateWidth(text: string, tracking = 0): number {
  let w = 0;
  for (const ch of text) {
    if (ch === ' ') w += 0.27;
    else if (NARROW.has(ch)) w += 0.31;
    else if (WIDE.has(ch)) w += 0.87;
    else if (ch >= 'A' && ch <= 'Z') w += 0.68;
    else w += 0.53;
    w += tracking;
  }
  return w;
}

/** Cost weights. Tuned together — see scoreLines for what each one buys. */
const RAGGED_WEIGHT = 6;
const OVERFLOW_WEIGHT = 400;
const UNDERFILL_WEIGHT = 1.2;
const WEAK_END_PENALTY = 1.2;
const WIDOW_PENALTY = 1.5;
const LINE_PENALTY = 0.5;

/**
 * Measures a string's width in em units (its width at font-size 1).
 *
 * The engine defaults to `estimateWidth`, a metric-free approximation, so
 * layout stays pure and testable without a DOM. The app injects a canvas-backed
 * measurer instead, which uses the real Helvetica Neue metrics — the estimate
 * under-reads heavy uppercase badly enough to push a headline off the edge.
 */
export type TextMeasurer = (text: string, tracking: number) => number;

export interface BalanceOptions {
  /** Target line count. The optimizer may use fewer if the text is short. */
  maxLines: number;
  /** Available width in em units at the current font size. */
  targetEmWidth: number;
  tracking: number;
  /** Defaults to the built-in estimate. */
  measure?: TextMeasurer;
}

interface Candidate {
  lines: string[];
  cost: number;
}

/**
 * Break text into visually balanced lines.
 *
 * Cost model (lower is better), all terms scale-invariant:
 *   - raggedness: squared deviation of each line's width from the mean,
 *     expressed as a FRACTION of the mean. Measuring in absolute em units
 *     instead would make the metric reward more, shorter lines — splitting
 *     text further mechanically shrinks the absolute spread — so a two-line
 *     balanced break would lose to a three-line ragged one.
 *   - overflow past the available measure, weighted heavily
 *   - under-fill: leaving most of the measure empty, which is what stops a
 *     headline being broken into more lines than it needs
 *   - ending a line on a function word ("of", "to", "your")
 *   - a very short final line (a widow)
 */
export function balanceLines(text: string, opts: BalanceOptions): string[] {
  const words = tokenize(text);
  if (words.length === 0) return [];
  if (words.length === 1) return [words[0]];

  const maxLines = Math.max(1, Math.min(opts.maxLines, words.length));
  let best: Candidate | null = null;

  // Enumerate every way to split N words into 1..maxLines lines. Headlines are
  // short by nature; we cap the search so a pasted paragraph degrades to a
  // greedy fill instead of hanging.
  if (words.length > 14) return greedyFill(words, opts);

  const measure = opts.measure ?? estimateWidth;
  const widths = words.map((w) => measure(w, opts.tracking));
  const spaceW = measure(' ', opts.tracking);

  const search = (start: number, current: number[][], depth: number) => {
    if (start === words.length) {
      const lines = current.map((idxs) => idxs.map((i) => words[i]).join(' '));
      const cost = scoreLines(lines, widths, spaceW, current, opts);
      if (!best || cost < best.cost) best = { lines, cost };
      return;
    }
    if (depth >= maxLines) return;
    // Take anything from one word up to all that remain. `maxLines` is a
    // ceiling, not a quota — reserving a word per allowed line would make
    // every split that uses FEWER lines unreachable, so a headline that fits
    // on one line could never be given one.
    const maxTake = words.length - start;
    for (let take = 1; take <= maxTake; take++) {
      const idxs: number[] = [];
      for (let i = 0; i < take; i++) idxs.push(start + i);
      current.push(idxs);
      search(start + take, current, depth + 1);
      current.pop();
    }
  };

  search(0, [], 0);

  const result = best ? (best as Candidate).lines : greedyFill(words, opts);
  assertPreservesWords(text, result);
  return result;
}

function scoreLines(
  lines: string[],
  widths: number[],
  spaceW: number,
  groups: number[][],
  opts: BalanceOptions,
): number {
  const lineWidths = groups.map((g) => {
    let w = 0;
    for (let i = 0; i < g.length; i++) {
      w += widths[g[i]];
      if (i < g.length - 1) w += spaceW;
    }
    return w;
  });

  const mean = lineWidths.reduce((a, b) => a + b, 0) / lineWidths.length;
  const target = Math.max(opts.targetEmWidth, 0.001);
  const safeMean = Math.max(mean, 0.001);
  let cost = 0;

  for (const w of lineWidths) {
    cost += Math.pow((w - mean) / safeMean, 2) * RAGGED_WEIGHT;
    if (w > opts.targetEmWidth) {
      cost += Math.pow((w - opts.targetEmWidth) / target, 2) * OVERFLOW_WEIGHT;
    }
  }

  // Under-fill, measured on the widest line: a break that leaves most of the
  // column empty is a break that did not need to happen.
  const widest = Math.max(...lineWidths);
  if (widest < opts.targetEmWidth) {
    cost += Math.pow((opts.targetEmWidth - widest) / target, 2) * UNDERFILL_WEIGHT;
  }

  // Function-word endings on every line but the last.
  for (let i = 0; i < lines.length - 1; i++) {
    const last = lines[i].split(' ').pop() ?? '';
    if (WEAK_ENDINGS.has(last.toLowerCase().replace(/[^a-z']/g, ''))) cost += WEAK_END_PENALTY;
  }

  // Widow: a final line far shorter than the others.
  if (lineWidths.length > 1) {
    const lastW = lineWidths[lineWidths.length - 1];
    if (lastW < mean * 0.38) cost += WIDOW_PENALTY;
  }

  cost += lines.length * LINE_PENALTY;

  return cost;
}

function greedyFill(words: string[], opts: BalanceOptions): string[] {
  const lines: string[] = [];
  let current: string[] = [];
  const measure = opts.measure ?? estimateWidth;
  for (const word of words) {
    const trial = [...current, word].join(' ');
    if (
      current.length > 0 &&
      measure(trial, opts.tracking) > opts.targetEmWidth &&
      lines.length < opts.maxLines - 1
    ) {
      lines.push(current.join(' '));
      current = [word];
    } else {
      current.push(word);
    }
  }
  if (current.length) lines.push(current.join(' '));
  return lines;
}

/**
 * Guard against the one failure mode that would be unacceptable: altering the
 * user's copy. Thrown errors are caught by the caller and fall back to the
 * unbroken string.
 */
export function assertPreservesWords(original: string, lines: string[]): void {
  const a = tokenize(original).join(' ');
  const b = tokenize(lines.join(' ')).join(' ');
  if (a !== b) {
    throw new Error(
      `Line breaking altered the text. Expected "${a}" but produced "${b}".`,
    );
  }
}

export function safeBalance(text: string, opts: BalanceOptions): string[] {
  if (!text.trim()) return [];
  try {
    return balanceLines(text, opts);
  } catch {
    return [text.trim()];
  }
}

/**
 * Pick words worth emphasizing. Returns indices into the whitespace-split
 * headline so the renderer can color them without touching the string.
 *
 * Heuristic: prefer the longest content words in the first line, cap at two,
 * and never emphasize more than a third of the headline (past that it stops
 * reading as emphasis).
 */
export function pickEmphasisWords(text: string, max = 2): number[] {
  const words = tokenize(text);
  if (words.length < 3) return [];

  const scored = words
    .map((word, index) => {
      const clean = word.toLowerCase().replace(/[^a-z0-9']/g, '');
      if (!clean || STOPWORDS.has(clean)) return null;
      // Earlier + longer wins. Front-loaded emphasis reads as a lead-in
      // rather than as random highlighting.
      const positionBonus = 1 - index / words.length;
      return { index, score: clean.length * 0.6 + positionBonus * 3 };
    })
    .filter((v): v is { index: number; score: number } => v !== null)
    .sort((a, b) => b.score - a.score);

  const limit = Math.min(max, Math.floor(words.length / 3));
  return scored
    .slice(0, Math.max(0, limit))
    .map((s) => s.index)
    .sort((a, b) => a - b);
}

/** Map word indices to per-line runs so the renderer can draw mixed styles. */
export interface StyledRun {
  text: string;
  emphasized: boolean;
}

export function linesToRuns(
  lines: string[],
  emphasisIndices: number[],
): StyledRun[][] {
  const set = new Set(emphasisIndices);
  let cursor = 0;
  return lines.map((line) => {
    const words = tokenize(line);
    const runs: StyledRun[] = [];
    for (const word of words) {
      const emphasized = set.has(cursor);
      const previous = runs[runs.length - 1];
      // Merge adjacent same-style words so measurement keeps native kerning
      // across the run rather than per word.
      if (previous && previous.emphasized === emphasized) {
        previous.text += ` ${word}`;
      } else {
        runs.push({ text: word, emphasized });
      }
      cursor++;
    }
    return runs;
  });
}

export function applyTransform(
  text: string,
  transform: 'none' | 'upper' | 'lower',
): string {
  if (transform === 'upper') return text.toUpperCase();
  if (transform === 'lower') return text.toLowerCase();
  return text;
}
